import { Description, Dialog, DialogPanel, DialogTitle } from "@headlessui/react";
import { useQueryClient } from "@tanstack/react-query";

import { JSX, useState } from "react";
import { useTranslation } from "react-i18next";

import Button from "@/components/buttons/Button.tsx";
import { IconTrash } from "@/components/icons";

import { USER_KEYS, useDeleteCreditCard } from "@/hooks/api/user.hooks";
import { useLang } from "@/hooks/useLang";
import { onError } from "@/utils/onError";
import { onSuccess } from "@/utils/onSuccess";

const DeleteCardModal = ({ id }: { id: number }): JSX.Element => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const { mutate } = useDeleteCreditCard();
  const queryClient = useQueryClient();
  const lang = useLang();

  const handleClose = () => setIsOpen(false);

  const handleDelete = () => {
    mutate(id, {
      onSuccess() {
        queryClient.invalidateQueries({ queryKey: USER_KEYS.creditCards(lang) });

        handleClose();

        onSuccess(t("cards.delete-success"));
      },
      onError,
    });
  };

  return (
    <>
      <button
        type={"button"}
        aria-label={t("Payment.delete-card")}
        onClick={() => setIsOpen(true)}
        className={"group rounded-full"}
      >
        <IconTrash className={"size-6 fill-none stroke-red-1 transition-colors group-hover:stroke-orange-1"} />
      </button>
      <Dialog transition open={isOpen} onClose={handleClose} className={"staff-modal-dialog"}>
        <DialogPanel transition className={"staff-modal flex max-w-[34.25rem] flex-col items-center gap-6"}>
          <div className={"max-w-[26.5rem] text-center"}>
            <DialogTitle as={"h2"} className={"staff-modal__title"}>
              {t("Payment.delete-card")}?
            </DialogTitle>
            <Description className={"staff-modal__description"}>{t("Payment.delete-card-message")}</Description>
          </div>
          <div className={"grid w-full grid-cols-2 gap-6"}>
            <Button color={"white"} aria-label={t("Actions.cancel")} onClick={handleClose} className={"w-full"}>
              {t("Actions.cancel")}
            </Button>
            <Button aria-label={t("Actions.delete")} onClick={handleDelete} className={"w-full"}>
              {t("Actions.delete")}
            </Button>
          </div>
        </DialogPanel>
      </Dialog>
    </>
  );
};

export default DeleteCardModal;
